import { useState, useRef, useEffect } from 'react'

export const useUploadStats = () => {
  const [uploadStats, setUploadStats] = useState({
    progress: 0,
    uploadSpeed: 0,
    uploadedBytes: 0,
    totalBytes: 0,
    timeRemaining: null
  })
  const progressHistoryRef = useRef([])
  const startTimeRef = useRef(null)
  const animationFrameRef = useRef(null)

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [])

  const uploadProgress = ({ loaded, total }, { size }, isMultipartUpload = false) => {
    const currentTime = Date.now()
    const currentTotalBytes = size ?? total
    if (!Number.isFinite(currentTotalBytes) || currentTotalBytes <= 0) return

    if (startTimeRef.current === null) {
      startTimeRef.current = currentTime
    }

    let currentUploadedBytes
    if (isMultipartUpload) {
      currentUploadedBytes = Math.round((loaded / 100) * currentTotalBytes)
    } else {
      currentUploadedBytes = loaded
    }

    const currentProgress = Math.min(100, Math.max(0, Math.round((currentUploadedBytes / currentTotalBytes) * 100)))

    progressHistoryRef.current.push({ time: currentTime, bytes: currentUploadedBytes })

    const WINDOW_MS = 3000
    while (progressHistoryRef.current.length > 0 && currentTime - progressHistoryRef.current[0].time > WINDOW_MS) {
      progressHistoryRef.current.shift()
    }

    let currentSpeed = 0
    if (progressHistoryRef.current.length > 1) {
      const oldest = progressHistoryRef.current[0]
      const newest = progressHistoryRef.current[progressHistoryRef.current.length - 1]
      const timeDiff = (newest.time - oldest.time) / 1000
      const bytesDiff = newest.bytes - oldest.bytes

      if (timeDiff > 0) {
        currentSpeed = Math.max(0, bytesDiff) / timeDiff
      }
    } else if (startTimeRef.current) {
      const totalElapsed = (currentTime - startTimeRef.current) / 1000
      if (totalElapsed > 0) {
        currentSpeed = currentUploadedBytes / totalElapsed
      }
    }

    let timeRemaining = null
    if (currentSpeed > 0) {
      const remainingBytes = currentTotalBytes - currentUploadedBytes
      if (remainingBytes > 0) {
        timeRemaining = remainingBytes / currentSpeed
      }
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
    animationFrameRef.current = requestAnimationFrame(() => {
      setUploadStats((prevStats) => {
        if (currentProgress > prevStats.progress || currentUploadedBytes > prevStats.uploadedBytes) {
          return {
            progress: currentProgress,
            uploadSpeed: currentSpeed || prevStats.uploadSpeed,
            uploadedBytes: currentUploadedBytes,
            totalBytes: currentTotalBytes,
            timeRemaining: timeRemaining !== null ? timeRemaining : prevStats.timeRemaining
          }
        }
        return prevStats
      })
    })
  }

  const resetStats = () => {
    const now = Date.now()
    startTimeRef.current = now
    progressHistoryRef.current = []
    setUploadStats({
      progress: 0,
      uploadSpeed: 0,
      uploadedBytes: 0,
      totalBytes: 0,
      timeRemaining: null
    })
  }

  return {
    uploadStats,
    uploadProgress,
    resetStats
  }
}
